import {
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  DefaultMap,
  getFirstElement,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';
import * as corgi from 'external/dev_april_corgi+/js/corgi';

import {
  DataConstraints,
  Graph,
  Node,
  Pad,
  padsForSubgraphNode,
} from './execution/graph';
import {MIXED_TREATMENT} from './nodes/mixing_transformer';
import {RELABELED_TREATMENT} from './nodes/subgraph_nodes';
import {
  ConcentrationValue,
  DataValue,
  MaterialId,
  MaterialValue,
  VolumeValue,
} from './types/data';
import {formatUnit, harmonizeUnits} from './types/units';

export function makeNodeDetail(graph: Graph, node: Node) {
  return <NodeDetail graph={graph} node={node} />;
}

function NodeDetail({graph, node}: {graph: Graph; node: Node}) {
  let inputs;
  let outputs;
  if (node.behavior.kind === 'subgraph') {
    const pads = padsForSubgraphNode(node, graph);
    inputs = pads.inputs;
    outputs = pads.outputs;
  } else {
    inputs = node.inputs;
    outputs = node.outputs;
  }
  const errors =
    graph.overlay?.errorsForNode(node)?.map(e => <li>{e}</li>) ?? [];
  const notes = graph.overlay?.notesForNode(node.id) ?? [];
  return (
    <div className="bg-neutral-800 overflow-y-auto oy-2 p-4 rounded">
      <div className="oy-1">
        {errors.length > 0 ? (
          <>
            <div>errors:</div>
            <div className="border p-2 rounded">
              <ul className="list-disc list-inside">{errors}</ul>
            </div>
          </>
        ) : (
          <></>
        )}
        {notes.length > 0 ? <div>notes:</div> : <></>}
        {[...notes].map(({label, value}) => (
          <div className="border p-2 rounded">
            <div className="font-bold">{label}</div>
            <table>
              <thead className="font-bold">
                <tr className="bg-neutral-700">
                  {[...value.values.keys()].map(k => (
                    <th className="border border-neutral-600 px-2 text-center">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getFirstElement(value.values.values()).map((_, i) => (
                  <tr className="even:bg-neutral-700 odd:bg-neutral-800">
                    {[...value.values.keys()].map(k => (
                      <td className="border border-neutral-600 px-2">
                        <TerseValue
                          value={checkExists(value.values.get(k))[i]}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div>outputs:</div>
        {[...outputs.values()].map(p => (
          <OutputPadDetail graph={graph} pad={p} />
        ))}

        <div>inputs:</div>
        {[...inputs.values()].map(p => (
          <div className="border p-2 rounded">
            <div className="font-bold">{p.label}</div>
            {p.links.size > 0 ? (
              <div>incoming links: {p.links.size}</div>
            ) : p.unlinked ? (
              <div>
                <div>
                  value: <TerseValue value={p.unlinked.value} />
                </div>
                <div>
                  constraints:{' '}
                  <Constraints constraints={p.unlinked.constraints} />
                </div>
              </div>
            ) : (
              <div>no links or value</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OutputPadDetail({graph, pad}: {graph: Graph; pad: Pad<'output'>}) {
  const value = graph.overlay?.sourceForPad(pad)?.lastValue();
  let displayed;
  try {
    displayed = value ? <ExtendedValue value={value} /> : 'unknown';
  } catch (e: unknown) {
    console.error('An error occurred while showing the value', value, e);
    displayed = '(an error occurred while showing this value)';
  }
  return (
    <div className="border p-2 rounded">
      <div className="font-bold">{pad.label}</div>
      <div>value: {displayed}</div>
    </div>
  );
}

function ExtendedValue({value}: {value: DataValue}) {
  if (value.kind === 'array') {
    return (
      <div className="oy-2">
        {value.elements.map(e => (
          <ExtendedValue value={e} />
        ))}
      </div>
    );
  } else if (value.kind === 'material') {
    return <ExtendedMaterialValue value={value} />;
  } else if (value.kind === 'plated_material') {
    return (
      <div>
        <div>
          <ExtendedMaterialValue
            value={value.material}
            note={`(in ${value.plate}-${value.well})`}
          />
        </div>
      </div>
    );
  } else {
    return <TerseValue value={value} />;
  }
}

function ExtendedMaterialValue({
  value,
  note,
}: {
  value: MaterialValue;
  note?: string;
}) {
  const f = [...flattenMaterial(value).values()];
  f.sort(({label: a}, {label: b}) =>
    a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()),
  );
  const contents = [];
  for (const {label, concentration, volume} of f) {
    const pieces = [];
    if (concentration) {
      pieces.push(<TerseValue value={concentration} />);
    }
    if (volume) {
      pieces.push(<TerseValue value={volume} />);
    }
    let fragment;
    if (pieces.length === 2) {
      fragment = (
        <>
          {pieces[0]}, {pieces[1]}
        </>
      );
    } else if (pieces.length === 1) {
      fragment = pieces[0];
    }
    contents.push(
      <tr className="even:bg-neutral-700 odd:bg-neutral-800">
        <td className="border border-neutral-600 px-2">{label}</td>
        <td className="border border-neutral-600 px-2">{fragment}</td>
      </tr>,
    );
  }

  return (
    <div>
      <div className="font-bold">
        {value.label}
        {note ? ` ${note}` : ''}
      </div>
      <table>
        <thead>
          <tr className="bg-neutral-700">
            <th className="border border-neutral-600 px-2 text-center">
              Content
            </th>
            <th className="border border-neutral-600 px-2 text-center">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>{contents}</tbody>
      </table>
    </div>
  );
}

function flattenMaterial(material: MaterialValue): DefaultMap<
  MaterialId,
  {
    label: string;
    concentration: ConcentrationValue | undefined;
    volume: VolumeValue | undefined;
  }
> {
  const flattened = new DefaultMap<
    MaterialId,
    {
      label: string;
      concentration: ConcentrationValue | undefined;
      volume: VolumeValue | undefined;
    }
  >(() => ({label: 'unknown', concentration: undefined, volume: undefined}));
  const passThrough = [MIXED_TREATMENT, RELABELED_TREATMENT, 'thermocycled'];
  if (passThrough.indexOf(material.tags.treatment as any) >= 0) {
    for (const content of material.contents) {
      mergeMaterialAndFlatten(content, /* scale= */ 1, flattened);
    }
  } else if (material.tags.treatment === 'reduced') {
    const original = getOnlyElement(material.contents);
    const to = material.amount;
    if (to.kind === 'concentration') {
      const from = original.amount;
      if (from.kind !== 'concentration') {
        throw new Error('Expected a concentration');
      }
      const harmonized = harmonizeUnits(from.unit, to.unit);
      if (!harmonized) {
        throw new Error(
          `Concentration ${formatConcentration(from)} doesn't ` +
            `harmonize with ${formatConcentration(to)}`,
        );
      }
      const scale = ((to.number / from.number) * harmonized[1]) / harmonized[0];
      mergeMaterialAndFlatten(original, scale, flattened);
    } else if (to.kind === 'volume') {
      mergeAmounts(original.id, original.label, to, /* scale= */ 1, flattened);
    } else {
      throw checkExhaustive(to);
    }
  } else {
    mergeAmounts(
      material.id,
      material.label,
      material.amount,
      /* scale= */ 1,
      flattened,
    );
  }
  return flattened;
}

function mergeMaterialAndFlatten(
  material: MaterialValue,
  scale: number,
  flattened: DefaultMap<
    MaterialId,
    {
      label: string;
      concentration: ConcentrationValue | undefined;
      volume: VolumeValue | undefined;
    }
  >,
): void {
  const inner = flattenMaterial(material);
  for (const [id, {label, concentration, volume}] of inner) {
    if (concentration) {
      mergeAmounts(id, label, concentration, scale, flattened);
    }
    if (volume) {
      mergeAmounts(id, label, volume, scale, flattened);
    }
  }
}

function mergeAmounts(
  id: MaterialId,
  label: string,
  amount: ConcentrationValue | VolumeValue,
  scale: number,
  flattened: DefaultMap<
    MaterialId,
    {
      label: string;
      concentration: ConcentrationValue | undefined;
      volume: VolumeValue | undefined;
    }
  >,
): void {
  const f = flattened.get(id);
  f.label = label;
  if (amount.kind === 'concentration') {
    if (!f.concentration) {
      f.concentration = {kind: 'concentration', number: 0, unit: amount.unit};
    }
    const harmonized = harmonizeUnits(amount.unit, f.concentration.unit);
    if (!harmonized) {
      throw new Error(
        `Concentration ${formatConcentration(f.concentration)} doesn't ` +
          `harmonize with ${formatConcentration(amount)}`,
      );
    }
    f.concentration.number +=
      (scale * amount.number * harmonized[1]) / harmonized[0];
  } else if (amount.kind === 'volume') {
    if (!f.volume) {
      f.volume = {kind: 'volume', numberNl: 0, preferredUnit: 'ul'};
    }
    f.volume.numberNl += scale * amount.numberNl;
  } else {
    throw checkExhaustive(amount);
  }
}

export function makePadTooltip(graph: Graph, pad: Pad<'input' | 'output'>) {
  return <PadTooltip graph={graph} pad={pad} />;
}

function PadTooltip({
  graph,
  pad,
}: {
  graph: Graph;
  pad: Pad<'input' | 'output'>;
}) {
  return (
    <div>
      <div>
        {pad.links.size > 0 ? (
          <div>
            {pad.links.size}{' '}
            {pad.direction === 'input' ? 'incoming' : 'outgoing'} link
            {pad.links.size > 1 ? 's' : ''}
          </div>
        ) : pad.unlinked ? (
          <div>
            <div>
              value: <TerseValue value={pad.unlinked.value} />
            </div>
            <div>
              constraints:{' '}
              <Constraints constraints={pad.unlinked.constraints} />
            </div>
          </div>
        ) : (
          <div>no links{pad.direction === 'input' ? ' or value' : ''}</div>
        )}
      </div>
      <div>
        <ul className="list-disc list-inside">
          {(graph.overlay?.errorsForPad(pad) ?? []).map(e => (
            <li>{e}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Constraints({constraints}: {constraints: DataConstraints}) {
  const elements = [];
  if (constraints.array) {
    for (const e of constraints.array) {
      elements.push(
        <li>
          <Constraints constraints={e} />
        </li>,
      );
    }
  }
  if (constraints.plate) {
    for (const [id, c] of constraints.plate.wells) {
      if (c.volume) {
        elements.push(
          <li>
            {id}: {formatVolume(c.volume.numberNl)}
          </li>,
        );
      }
    }
  }
  if (constraints.volume) {
    elements.push(<li>{formatVolume(constraints.volume.numberNl)}</li>);
  }

  if (elements.length > 0) {
    return <ul className="list-disc list-inside ms-4">{elements}</ul>;
  } else {
    return '(none)';
  }
}

function TerseValue({value}: {value: DataValue}) {
  if (value.kind === 'array') {
    return (
      <ul className="list-disc list-inside">
        {value.elements.map(e => (
          <li>
            <TerseValue value={e} />
          </li>
        ))}
      </ul>
    );
  } else if (value.kind === 'concentration') {
    return formatConcentration(value);
  } else if (value.kind === 'material') {
    let amount;
    if (value.amount.kind === 'concentration') {
      amount = formatConcentration(value.amount);
    } else if (value.amount.kind === 'volume') {
      amount = formatVolume(value.amount.numberNl);
    } else {
      throw checkExhaustive(value.amount);
    }
    return `${amount} of ${value.label}`;
  } else if (value.kind === 'plate') {
    return `id: ${value.id} contents: ${value.wells.size}`;
  } else if (value.kind === 'plated_material') {
    return (
      <span>
        <TerseValue value={value.material} /> on {value.plate}-{value.well}
      </span>
    );
  } else if (value.kind === 'script') {
    return `(${value.value.split('\n').length} line script)`;
  } else if (value.kind === 'select') {
    return value.value;
  } else if (value.kind === 'string') {
    return value.value;
  } else if (value.kind === 'volume') {
    return formatVolume(value.numberNl);
  } else {
    return JSON.stringify(value);
  }
}

export function formatConcentration(
  value: ConcentrationValue,
  round: boolean = true,
): string {
  let space;
  if (value.unit === 'x' || value.unit === '%') {
    space = '';
  } else {
    space = ' ';
  }
  const unit = space + formatUnit(value.unit);
  if (round) {
    return `${Math.round(value.number * 10_000) / 10_000}${unit}`;
  } else {
    return `${value.number}${unit}`;
  }
}

export function formatVolume(volumeNl: number, round: boolean = true): string {
  if (round) {
    return `${Math.round(volumeNl / 10) / 100}ul`;
  } else {
    return `${volumeNl / 1000}ul`;
  }
}
